/**
 * Pure finite state machine for the unlock lifecycle.
 *
 * No React, no DOM, no plugins — just a reducer + types. The hook in
 * `useUnlockMachine.ts` wires this up to IdentityBoot, AuthContext, and
 * Biometrics, and handles the side effects (calling `authenticate()`,
 * triggering logout, listening to app lifecycle).
 *
 * The single source of truth for "what should the user see right now?"
 * was previously spread across PinManager's effects, refs, and render
 * branches. Consolidating it here means:
 *
 *   1. The decision tree (decideUnlock) runs identically on initial boot
 *      and on app-resume re-lock — no two divergent reactive copies.
 *   2. Every transition is a named, loggable, testable event.
 *   3. PinManager becomes a pure renderer that maps `state` → JSX.
 */

export type UnlockState =
  | { kind: 'booting' }
  | { kind: 'signed-out' }
  | { kind: 'awaiting-pin-setup' }
  | { kind: 'attempting-biometric' }
  | { kind: 'awaiting-pin-entry'; attempts: number }
  | { kind: 'logging-out' }
  | { kind: 'unlocked' };

export interface UnlockContext {
  /** PIN and biometrics are native-only concerns. On web the machine
   *  goes straight from `signed-out` to `unlocked` without touching
   *  `pinSet` / `biometric*`. */
  isNative: boolean;
  isLoggedIn: boolean;
  pinSet: boolean;
  biometricAvailable: boolean;
  biometricEnabled: boolean;
}

export type UnlockAction =
  | { type: 'EVALUATE'; ctx: UnlockContext }
  | { type: 'LOGGED_OUT' }
  | { type: 'BIOMETRIC_SUCCEEDED' }
  | { type: 'BIOMETRIC_FAILED' }
  | { type: 'PIN_CORRECT' }
  | { type: 'PIN_WRONG' }
  | { type: 'PIN_SETUP_COMPLETED' };

export const INITIAL_UNLOCK_STATE: UnlockState = { kind: 'booting' };

const MAX_PIN_ATTEMPTS = 3;

/**
 * The unlock decision tree, factored out so `EVALUATE` (initial boot) and
 * any future "re-evaluate from scratch" action share one code path.
 *
 *   not logged in              → signed-out
 *   logged in on web           → unlocked  (PIN/bio are native-only)
 *   no PIN                     → awaiting-pin-setup
 *   bio available + enabled    → attempting-biometric
 *   otherwise                  → awaiting-pin-entry (attempts: 0)
 */
export const decideUnlock = (ctx: UnlockContext): UnlockState => {
  if (!ctx.isLoggedIn) return { kind: 'signed-out' };
  if (!ctx.isNative) return { kind: 'unlocked' };
  if (!ctx.pinSet) return { kind: 'awaiting-pin-setup' };
  if (ctx.biometricAvailable && ctx.biometricEnabled) {
    return { kind: 'attempting-biometric' };
  }
  return { kind: 'awaiting-pin-entry', attempts: 0 };
};

export const unlockReducer = (
  state: UnlockState,
  action: UnlockAction,
): UnlockState => {
  switch (action.type) {
    case 'EVALUATE':
      return decideUnlock(action.ctx);

    case 'LOGGED_OUT':
      return { kind: 'signed-out' };

    case 'BIOMETRIC_SUCCEEDED':
      // Only honor a bio result when we were actually waiting for one.
      // Defends against a late resolve after the user already moved on
      // (e.g. cancelled the prompt and switched to PIN entry).
      if (state.kind !== 'attempting-biometric') return state;
      return { kind: 'unlocked' };

    case 'BIOMETRIC_FAILED':
      if (state.kind !== 'attempting-biometric') return state;
      return { kind: 'awaiting-pin-entry', attempts: 0 };

    case 'PIN_CORRECT':
      if (state.kind !== 'awaiting-pin-entry') return state;
      return { kind: 'unlocked' };

    case 'PIN_WRONG': {
      if (state.kind !== 'awaiting-pin-entry') return state;
      const nextAttempts = state.attempts + 1;
      if (nextAttempts >= MAX_PIN_ATTEMPTS) {
        return { kind: 'logging-out' };
      }
      return { kind: 'awaiting-pin-entry', attempts: nextAttempts };
    }

    case 'PIN_SETUP_COMPLETED':
      if (state.kind !== 'awaiting-pin-setup') return state;
      return { kind: 'unlocked' };
  }
};
